import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, ArrowRight, CreditCard, BarChart3, Wallet, Sparkles, CheckCircle, Star, Mic, Heart } from 'lucide-react';
import { Button } from './ui/button';
import AnimatedCounter from './ui/AnimatedCounter';

export const HeroSection = () => {
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      text: "SaaS-Base cut our time-to-market by 60%. The built-in monetization studio is a game-changer.",
      author: "Sarah M.",
      role: "CTO, TechFlow"
    },
    {
      text: "Finally, a platform that handles subscriptions, usage metering, and governance in one place.",
      author: "Michael R.", 
      role: "Product Lead, CloudScale"
    },
    {
      text: "The Stripe integration and prepaid wallet system saved us months of development time.",
      author: "Emma L.",
      role: "CEO, DataStream"
    }
  ];

  // Rotate testimonials every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    // Wrapper gradient with dark variants applied; spacing only (no min-h-screen) per spec
    <section className="relative bg-gradient-to-br from-indigo-50 via-white to-cyan-50 flex items-center pt-10 pb-20 dark:from-slate-950 dark:via-slate-900 dark:to-slate-800">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 dark:opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23667eea' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>
      
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Copy & CTAs */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Social Proof */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="flex items-center space-x-4"
            >
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-400 to-purple-500 border-2 border-white dark:border-gray-800 shadow-sm flex items-center justify-center text-white text-xs font-semibold"
                  >
                    {String.fromCharCode(64 + i)}
                  </div>
                ))}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                Trusted by <AnimatedCounter end={500} duration={2000} />+ SaaS companies
              </div>
            </motion.div>

            {/* Main Headline */}
            <div className="space-y-4">
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="text-4xl lg:text-6xl font-bold text-gray-900 dark:text-gray-100 leading-tight"
              >
                Build & Scale Your
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-cyan-600">
                  SaaS Business
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-2xl"
              >
                Enterprise-grade monetization platform with subscription management, usage metering, 
                prepaid wallets, and complete pricing governance. Launch faster, scale effortlessly, 
                monetize smarter with built-in Stripe integration.
              </motion.p>
            </div>

            {/* Key Benefits */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-4"
            >
              <div className="flex items-center space-x-3 bg-white/80 backdrop-blur-sm rounded-lg p-4 shadow-sm border border-gray-100 dark:bg-white/5 dark:border-gray-700">
                <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/40 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">Flexible Billing</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Subscriptions & prepaid</p>
                </div>
              </div>

              <div className="flex items-center space-x-3 bg-white/80 backdrop-blur-sm rounded-lg p-4 shadow-sm border border-gray-100 dark:bg-white/5 dark:border-gray-700">
                <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900/40 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">Usage Metering</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Track consumption</p>
                </div>
              </div>

              <div className="flex items-center space-x-3 bg-white/80 backdrop-blur-sm rounded-lg p-4 shadow-sm border border-gray-100 dark:bg-white/5 dark:border-gray-700">
                <div className="w-10 h-10 bg-pink-100 dark:bg-pink-900/40 rounded-lg flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">Governance</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Complete control</p>
                </div>
              </div>
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.6 }}
              className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4"
            >
              <Link to="/auth/signup">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 text-lg"
                >
                  Start Your Journey
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              
              <Button
                size="lg"
                variant="outline"
                onClick={() => setVideoModalOpen(true)}
                className="w-full sm:w-auto border-indigo-200 text-indigo-700 hover:bg-indigo-50 px-8 py-4 text-lg dark:border-indigo-800 dark:text-indigo-300 dark:hover:bg-indigo-900/20"
              >
                <Play className="w-5 h-5 mr-2" />
                Watch Demo
              </Button>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.6 }}
              className="flex items-center space-x-6 text-sm text-gray-500 dark:text-gray-400"
            >
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Free 14-day trial</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Cancel anytime</span>
              </div>
            </motion.div>

            {/* Rotating Testimonial */}
            <motion.div
              key={currentTestimonial}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-r from-indigo-50 to-cyan-50 rounded-xl p-6 border border-indigo-100 dark:from-slate-900 dark:to-slate-800 dark:border-slate-700"
            >
              <div className="flex items-start space-x-4">
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <div className="flex-1">
                  <p className="text-gray-700 dark:text-gray-200 italic mb-2">
                    "{testimonials[currentTestimonial].text}"
                  </p>
                  <div className="text-sm">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {testimonials[currentTestimonial].author}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">
                      , {testimonials[currentTestimonial].role}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column - Hero Visual */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="relative"
          >
            {/* Main Demo Interface */}
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden dark:bg-gray-900 dark:border-gray-700">
              <div className="bg-gradient-to-r from-indigo-600 to-cyan-600 p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  <span className="text-white text-sm font-medium ml-4">
                    SaaS-Base
                  </span>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Voice Interface Demo */}
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-100 dark:from-purple-950 dark:to-indigo-950 dark:border-purple-900/50">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
                      <Mic className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-purple-900 dark:text-purple-200">You said:</div>
                      <div className="text-purple-700 dark:text-purple-200">"Who should I reach out to today?"</div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-4 border border-blue-100 dark:from-blue-950 dark:to-cyan-950 dark:border-blue-900/50">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-full flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-blue-900 mb-2 dark:text-blue-200">AI Suggestion:</div>
                      <div className="text-blue-700 space-y-2 dark:text-blue-200">
                        <p>"I notice you haven't talked to <strong>Sarah</strong> in 12 days. She mentioned her job interview last time - perfect opportunity to check in!"</p>
                        <div className="flex space-x-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                            High Priority
                          </span>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200">
                            Great Timing
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-3 pt-2">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-300">
                      <AnimatedCounter end={47} duration={1500} />
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Friends Tracked</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-cyan-600 dark:text-cyan-300">
                      <AnimatedCounter end={234} duration={1500} />
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Memories Saved</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-300">98%</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Satisfaction</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <motion.div
              animate={{ 
                y: [0, -10, 0],
                rotate: [0, 2, 0]
              }}
              transition={{ 
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full flex items-center justify-center shadow-lg"
            >
              <Heart className="w-8 h-8 text-white" />
            </motion.div>

            <motion.div
              animate={{ 
                y: [0, 15, 0],
                x: [0, 5, 0]
              }}
              transition={{ 
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1
              }}
              className="absolute -bottom-4 -left-4 w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg"
            >
              <Sparkles className="w-6 h-6 text-white" />
            </motion.div>
          </motion.div>
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 text-center"
        >
          <div>
            <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-300 mb-2">
              <AnimatedCounter end={15000} duration={2000} />+
            </div>
            <div className="text-gray-600 dark:text-gray-400">Active Users</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-cyan-600 dark:text-cyan-300 mb-2">
              <AnimatedCounter end={850000} duration={2000} />+
            </div>
            <div className="text-gray-600 dark:text-gray-400">Memories Saved</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-purple-600 dark:text-purple-300 mb-2">
              <AnimatedCounter end={98} duration={1500} />%
            </div>
            <div className="text-gray-600 dark:text-gray-400">User Satisfaction</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-pink-600 dark:text-pink-300 mb-2">
              <AnimatedCounter end={45} duration={1500} />%
            </div>
            <div className="text-gray-600 dark:text-gray-400">More Connected</div>
          </div>
        </motion.div>
      </div>

      {/* Video Modal */}
      {videoModalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setVideoModalOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            className="relative bg-white dark:bg-gray-900 rounded-xl overflow-hidden max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setVideoModalOpen(false)}
              className="absolute top-4 right-4 z-10 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white"
            >
              ×
            </button>
            <div className="aspect-video bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <div className="text-center text-white">
                <Play className="w-16 h-16 mx-auto mb-4" />
                <h3 className="text-2xl font-bold">Demo Video</h3>
                <p className="text-white/80">Coming Soon</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

export default HeroSection;